import { Asset } from '@/api/AssetApi';
import { UserDataProp } from '../types';
import {
	ConnectingSlot,
	ConnectingSlotData,
	SlotSide,
	SlotType,
} from './ConnectingSlot';
import { CreatorAction, UUID } from '@/types/types';
import { ProjectRenderer } from '../ProjectRenderer';
import { RENDERER_CONFIG } from '@/configs/rendererConfig';
import { RenderUtils } from '../RenderUtils';
import {
	Vector3Tuple,
	EulerTuple,
	Group,
	Object3D,
	Box3,
	Quaternion,
	Euler,
	Vector3,
} from 'three';

export interface SerializedSofaModule {
	id: UUID;
	objectData: {
		position: Vector3Tuple;
		rotation: EulerTuple;
	};
	materialActions: Record<string, CreatorAction>;
	connectingSlots: ConnectingSlotData[];
	asset: Asset;
}

const oppositeSides: Record<SlotSide, SlotSide> = {
	[SlotSide.LEFT]: SlotSide.RIGHT,
	[SlotSide.RIGHT]: SlotSide.LEFT,
	[SlotSide.BOTTOM]: SlotSide.TOP,
	[SlotSide.TOP]: SlotSide.BOTTOM,
};
const Y_AXIS = new Vector3(0, 1, 0);
const SCORE_EPSILON = 1e-6;

export class SofaModule {
	id: UUID;
	group = new Group();
	connectingSlots: ConnectingSlot[] = [];
	materialActions: Record<string, CreatorAction> = {};
	private modelObject?: Object3D;
	private renderer: ProjectRenderer;
	private asset: Asset;
	private _selected = false;
	private _hovered = false;

	constructor(
		renderer: ProjectRenderer,
		asset: Asset,
		id: UUID,
		object: Object3D
	) {
		this.renderer = renderer;

		this.id = id;
		this.asset = structuredClone(asset);

		this.group.userData = {
			[UserDataProp.IS_SOFA_MODULE]: true,
			[UserDataProp.SOFA_MODULE_ID]: id,
		};

		this.addModelToGroup(object);
	}

	private addModelToGroup(object: Object3D) {
		this.modelObject = object;
		this.group.add(object);
		this.group.traverse((obj) => {
			obj.layers.disable(0);
			obj.layers.enable(RENDERER_CONFIG.SELECTABLE_LAYER);
			obj.receiveShadow = true;
			obj.castShadow = true;
		});

		const box = new Box3().setFromObject(this.group);
		const minY = box.min.y;
		this.group.position.y += -minY;
	}

	get selected() {
		return this._selected;
	}

	set selected(value: boolean) {
		this._selected = value;

		if (value)
			this.renderer.postprocessing.outlinePass.selectedObjects =
				this.group.children;
		else
			this.renderer.postprocessing.outlinePass.selectedObjects =
				this.renderer.postprocessing.outlinePass.selectedObjects.filter(
					(obj) => {
						const group = RenderUtils.searchSofaModuleGroup(obj);
						if (!group) return true;

						return group.userData[UserDataProp.SOFA_MODULE_ID] !== this.id;
					}
				);
	}

	get hovered() {
		return this._hovered;
	}

	set hovered(value: boolean) {
		if (this.selected) return;

		this._hovered = value;

		if (value)
			this.renderer.postprocessing.outlinePass.selectedObjects.push(
				...this.group.children
			);
		else
			this.renderer.postprocessing.outlinePass.selectedObjects =
				this.renderer.postprocessing.outlinePass.selectedObjects.filter(
					(obj) => {
						const group = RenderUtils.searchSofaModuleGroup(obj);
						if (!group) return true;

						const selectedModule = this.renderer.getSelectedModule();
						if (
							selectedModule?.id === group.userData[UserDataProp.SOFA_MODULE_ID]
						)
							return true;

						return group.userData[UserDataProp.SOFA_MODULE_ID] !== this.id;
					}
				);
	}

	private isOppositeSide(sideA: SlotSide, sideB: SlotSide) {
		return oppositeSides[sideA] === sideB;
	}

	private normalizeAngle(angle: number) {
		const pi2 = Math.PI * 2;
		let normalized = angle % pi2;

		if (normalized > Math.PI) normalized -= pi2;
		if (normalized < -Math.PI) normalized += pi2;

		return normalized;
	}

	private getModuleCenter(sofaModule: SofaModule) {
		return new Box3().setFromObject(sofaModule.group).getCenter(new Vector3());
	}

	private getSlotWorldPosition(slot: ConnectingSlot) {
		const position = new Vector3();
		slot.point.getWorldPosition(position);
		return position;
	}

	private getAngleDiffY(targetSlot: ConnectingSlot, matchSlot: ConnectingSlot) {
		const targetRotation = new Quaternion();
		targetSlot.point.getWorldQuaternion(targetRotation);

		const matchRotation = new Quaternion();
		matchSlot.point.getWorldQuaternion(matchRotation);

		const targetEuler = new Euler().setFromQuaternion(targetRotation, 'YXZ');
		const matchEuler = new Euler().setFromQuaternion(matchRotation, 'YXZ');

		return this.normalizeAngle(targetEuler.y - matchEuler.y);
	}

	private alignModuleBySlots(
		targetSlot: ConnectingSlot,
		matchSlot: ConnectingSlot
	) {
		const angleDiffY = this.getAngleDiffY(targetSlot, matchSlot);
		this.group.rotateOnAxis(Y_AXIS, angleDiffY);

		const targetPosition = this.getSlotWorldPosition(targetSlot);
		const matchPosition = this.getSlotWorldPosition(matchSlot);

		const offset = new Vector3().subVectors(targetPosition, matchPosition);
		offset.y = 0;
		this.group.position.add(offset);

		return Math.abs(angleDiffY);
	}

	private getOutwardDirection(selectedSlot: ConnectingSlot) {
		const selectedModuleCenter = this.getModuleCenter(selectedSlot.hostModule);
		const selectedSlotPosition = this.getSlotWorldPosition(selectedSlot);
		const outwardDirection = selectedSlotPosition.sub(selectedModuleCenter);
		outwardDirection.y = 0;
		if (!outwardDirection.lengthSq()) return null;

		return outwardDirection.normalize();
	}

	private pickBestSlot(
		candidates: ConnectingSlot[],
		selectedSlot: ConnectingSlot
	) {
		if (!candidates.length) return null;
		if (candidates.length === 1) return candidates[0];

		const originalPosition = this.group.position.clone();
		const originalQuaternion = this.group.quaternion.clone();
		const selectedModuleCenter = this.getModuleCenter(selectedSlot.hostModule);
		const outwardDirection = this.getOutwardDirection(selectedSlot);

		let bestSlot = candidates[0];
		let bestOutwardScore = Number.NEGATIVE_INFINITY;
		let bestRotationScore = Number.POSITIVE_INFINITY;

		for (const candidate of candidates) {
			this.group.position.copy(originalPosition);
			this.group.quaternion.copy(originalQuaternion);

			const rotationScore = this.alignModuleBySlots(selectedSlot, candidate);
			const candidateCenter = this.getModuleCenter(this);
			const centerOffset = candidateCenter.sub(selectedModuleCenter);
			centerOffset.y = 0;

			let outwardScore = centerOffset.lengthSq();
			if (outwardDirection) outwardScore = centerOffset.dot(outwardDirection);

			const isBetterOutward = outwardScore > bestOutwardScore + SCORE_EPSILON;
			const isOutwardTie =
				Math.abs(outwardScore - bestOutwardScore) <= SCORE_EPSILON;
			const isBetterRotation = rotationScore < bestRotationScore;

			if (!isBetterOutward && !(isOutwardTie && isBetterRotation)) continue;

			bestSlot = candidate;
			bestOutwardScore = outwardScore;
			bestRotationScore = rotationScore;
		}

		this.group.position.copy(originalPosition);
		this.group.quaternion.copy(originalQuaternion);

		return bestSlot;
	}

	private disposeSlots() {
		for (const slot of this.connectingSlots) {
			slot.dispose();
		}
		this.connectingSlots = [];
	}

	private getConnectedNeighbors() {
		const seen = new Set<UUID>();
		const neighbors: SofaModule[] = [];

		for (const slot of this.connectingSlots) {
			if (!slot.connectedSlot) continue;

			const neighbor = slot.connectedSlot.hostModule;
			if (seen.has(neighbor.id)) continue;

			seen.add(neighbor.id);
			neighbors.push(neighbor);
		}

		return neighbors;
	}

	private connectToHostModule(selectedSlot: ConnectingSlot) {
		const accessorySlot = this.connectingSlots.find(
			(slot) => slot.slotType === SlotType.ACCESSORY
		);

		if (!accessorySlot) return;

		selectedSlot.connectTo(accessorySlot);
		this.alignModuleBySlots(selectedSlot, accessorySlot);
	}

	async createConnectingSlots(data?: SerializedSofaModule) {
		if (data) {
			for (const slotData of data.connectingSlots) {
				const slot = new ConnectingSlot(
					this.renderer,
					this,
					slotData.slotType as SlotType
				);
				await slot.setFromData(slotData);
				this.connectingSlots.push(slot);
			}
			return;
		}

		this.group.traverse((object) => {
			if (!object.userData[UserDataProp.IS_CONNECTING_SLOT]) return;

			const slotType = object.userData[UserDataProp.SLOT_TYPE];
			if (!slotType) throw new Error('MISSING_SLOT_TYPE');

			this.connectingSlots.push(
				new ConnectingSlot(this.renderer, this, slotType, object)
			);
		});
	}

	getOccupiedSlotsData() {
		const occupiedSlots = this.connectingSlots.filter(
			(slot) => !!slot.connectedSlot
		);

		const slotTypes = occupiedSlots.map((slot) => slot.slotType);
		const modulesIds = occupiedSlots
			.map((slot) => slot.connectedSlot?.hostModule.id!)
			.filter((id) => id !== this.id);

		return {
			slotTypes,
			modulesIds,
		};
	}

	connectBySlot() {
		const selectedSlot = this.renderer.selectedConnectingSlot;
		if (!selectedSlot) return;

		if (selectedSlot.slotType === SlotType.HOST) {
			this.connectToHostModule(selectedSlot);
			return;
		}

		const candidates = this.connectingSlots.filter((slot) =>
			slot.canConnectTo(selectedSlot)
		);

		if (!candidates.length) return;

		const oppositeCandidates = candidates.filter((slot) =>
			this.isOppositeSide(slot.slotSide, selectedSlot.slotSide)
		);

		const targetCandidates = oppositeCandidates.length
			? oppositeCandidates
			: candidates;

		const bestSlot = this.pickBestSlot(targetCandidates, selectedSlot);
		if (!bestSlot) return;

		selectedSlot.connectTo(bestSlot);
		this.alignModuleBySlots(selectedSlot, bestSlot);
	}

	isDeleteable() {
		const neighbors = this.getConnectedNeighbors();
		if (neighbors.length <= 1) return true;

		const visited = new Set<UUID>([this.id, neighbors[0].id]);
		const queue: SofaModule[] = [neighbors[0]];
		let queueIndex = 0;

		while (queueIndex < queue.length) {
			const currentModule = queue[queueIndex];
			queueIndex += 1;

			for (const slot of currentModule.connectingSlots) {
				if (!slot.connectedSlot) continue;

				const neighbor = slot.connectedSlot.hostModule;
				if (visited.has(neighbor.id)) continue;

				visited.add(neighbor.id);
				queue.push(neighbor);
			}
		}

		return neighbors.every((neighbor) => visited.has(neighbor.id));
	}

	alignToSlot(anchorSlot: ConnectingSlot, movingSlot: ConnectingSlot) {
		if (movingSlot.hostModule !== this) return;

		const targetRotation = new Quaternion();
		anchorSlot.point.getWorldQuaternion(targetRotation);

		const movingRotation = new Quaternion();
		movingSlot.point.getWorldQuaternion(movingRotation);

		const targetEuler = new Euler().setFromQuaternion(targetRotation, 'YXZ');
		const movingEuler = new Euler().setFromQuaternion(movingRotation, 'YXZ');

		const angleDiffY = targetEuler.y - movingEuler.y;

		this.group.rotateOnAxis(Y_AXIS, angleDiffY);

		const anchorPosition = new Vector3();
		anchorSlot.point.getWorldPosition(anchorPosition);

		const movingPosition = new Vector3();
		movingSlot.point.getWorldPosition(movingPosition);

		const offset = new Vector3().subVectors(anchorPosition, movingPosition);
		offset.y = 0;
		this.group.position.add(offset);
	}

	swapModel(asset: Asset, object: Object3D) {
		if (this.modelObject) this.group.remove(this.modelObject);
		this.modelObject = object;
		this.disposeSlots();

		this.asset = structuredClone(asset);
		this.group.add(object);
	}

	dispose() {
		this.disposeSlots();

		RenderUtils.clearGroup(this.group);
		this.group.removeFromParent();
	}

	export(): SerializedSofaModule {
		return {
			id: this.id,
			asset: structuredClone(this.asset),
			materialActions: structuredClone(this.materialActions),
			objectData: {
				position: this.group.position.toArray(),
				rotation: this.group.rotation.toArray(),
			},
			connectingSlots: this.connectingSlots.map((slot) => slot.export()),
		};
	}
}
